import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component } from '@angular/core';
import { Router } from '@angular/router';
import {
    IOcbEnvironmentAccess,
    IOcbLocalAgent,
    LocalAgentsService,
    OcbAgentStatus,
    OcbAgentTypes,
    UserService
} from '@app/core/';
import { TranslateService } from '@ngx-translate/core';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { forkJoin, from as fromPromise } from 'rxjs';
import { filter, finalize, mergeMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-home-local-agents-management',
    templateUrl: './home-local-agents-management.component.html',
    styleUrls: ['./home-local-agents-management.component.scss'],
})
export class HomeLocalAgentsManagementComponent implements AfterViewInit {

    // Main data source (environments & local agents)
    public environments: IOcbEnvironmentAccess[];
    public localAgents: IOcbLocalAgent[] = new Array<IOcbLocalAgent>();

    // Tables to filter local agents
    public environmentsAccess = new Map<string, boolean>();
    public localAgentsStatus: OcbAgentStatus[] = new Array<OcbAgentStatus>();
    public localAgentsTypes: OcbAgentTypes[] = new Array<OcbAgentTypes>();

    /**
     * Boolean to show or hide the loading panel
     */
    public isLoadPanelVisible: boolean = false;

    /**
     * Status labels in JSON object
     */
    private statusLabels: any;

    constructor(
        private userService: UserService,
        private translateService: TranslateService,
        private localAgentsService: LocalAgentsService,
        private router: Router) {
        this.displayStatus = this.displayStatus.bind(this);
    }

    public ngAfterViewInit(): void {
        this.userService.user.subscribe(admin => {
            this.environments = admin.environments;
            this.environments.forEach(env => {
                this.environmentsAccess.set(env.environmentId, env.canManageLocalAgents);
            });
        });
        this.translateService.get('status').subscribe(labels => {
            this.statusLabels = labels;
            this.loadDataSource();
        });
    }

    /**
     * Set Recipients context menu items
     * @param e Handle event
     */
    public onContextMenuPreparing(e) {
        if (e.row && e.row.rowType === 'data') {
            e.items = [];
            if (this.environmentsAccess.get(e.row.data.environmentId) && e.row.data.currentStatus && e.row.data.agentType === OcbAgentTypes.OnPremise) {
                e.items.push({ text: this.translateService.instant('_commons.seeCommandsHistory'), icon: 'clock', onItemClick: (ev) => this.router.navigateByUrl(`/${e.row.data.environmentId}/local-agents-commands?agent=${e.row.data.id}`) });
                if (e.row.data.currentStatus !== OcbAgentStatus.Started) {
                    e.items.push({ text: this.translateService.instant('_commons.start'), icon: 'video', beginGroup: true, onItemClick: (ev) => this.startLocalAgent(ev, e.row.data.id) });
                }
                if (e.row.data.currentStatus !== OcbAgentStatus.Stopped) {
                    e.items.push({ text: this.translateService.instant('_commons.stop'), icon: 'square', beginGroup: true, onItemClick: (ev) => this.stopLocalAgent(ev, e.row.data.id) });
                    e.items.push({ text: this.translateService.instant('_commons.restart'), icon: 'refresh', onItemClick: (ev) => this.restartLocalAgent(ev, e.row.data.id) });
                }
            }
        }
    }

    /**
     * Start a local agent
     * @param e Handle event
     * @param localAgentId Internal identifier of the local agent to start
     */
    public startLocalAgent(e, localAgentId) {
        e.event.stopPropagation();
        forkJoin([
            this.translateService.get('localAgents.msgStartConfirm'),
            this.translateService.get('localAgents.msgStartConfirmTitle')
        ])
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the request
                mergeMap(_ => this.localAgentsService.start(localAgentId, true)),
                // Display confirm message
                mergeMap(_ => this.translateService.get('localAgents.msgStartComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                },
                error: (err) => this.handleError(err)
            });
    }

    /**
     * Stop a local agent
     * @param e Handle event
     * @param localAgentId Internal identifier of the local agent to stop
     */
    public stopLocalAgent(e, localAgentId) {
        e.event.stopPropagation();
        forkJoin([
            this.translateService.get('localAgents.msgStopConfirm'),
            this.translateService.get('localAgents.msgStopConfirmTitle')
        ])
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the RESET request
                mergeMap(_ => this.localAgentsService.stop(localAgentId, true)),
                // Display confirm message
                mergeMap(_ => this.translateService.get('localAgents.msgStopComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                },
                error: (err) => this.handleError(err)
            });
    }

    /**
     * Restart a local agent
     * @param e Handle event
     * @param localAgentId Internal identifier of the local agent to restart
     */
    public restartLocalAgent(e, localAgentId) {
        e.event.stopPropagation();
        forkJoin([
            this.translateService.get('localAgents.msgRestartConfirm'),
            this.translateService.get('localAgents.msgRestartConfirmTitle')
        ])
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the RESET request
                mergeMap(_ => this.localAgentsService.restart(localAgentId, true)),
                // Display confirm message
                mergeMap(_ => this.translateService.get('localAgents.msgRestartComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                },
                error: (err) => this.handleError(err)
            });
    }

    /**
     * Display value of status
     * @param rowData Current row data
     */
    public displayStatus(rowData) {
        return this.statusLabels[OcbAgentStatus[rowData.currentStatus]];
    }

    /**
     * Load all local agents
     */
    private loadDataSource() {
        this.localAgentsService.getLocalAgents(null, false).subscribe(localAgents => this.localAgents = localAgents);
    }

    /**
     * Displays error message in notification
     */
    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}
